import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { T, FONT_STACK } from '../customer/customers.theme';
import { GhostButton, PrimaryButton } from '../customer/CustomersUI';
import { COPY } from './settings.copy';
import {
  blankAccount,
  blankMessage,
  blankUnit,
  buildPayload,
  formFromBusiness,
  sameForm,
  signatureOf,
  validate,
  willCloseMenu,
  MAX_ACCOUNTS,
  MAX_SMS,
  MAX_SMS_CUSTOM,
  MAX_UNITS,
} from './settings.rules';
import { classifySaveError, saveBusinessSettings, serverMessage } from './settingsApi';
import { setActiveUser, setBusiness } from '../../SignupAndLogin/loginSlice';
import LogoCard from './LogoCard';
import RowsCard from './RowsCard';
import UnitsCard from './UnitsCard';
import {
  Card,
  Field,
  FieldError,
  Hint,
  SectionHead,
  SwitchRow,
  TYPE,
  WarnLine,
} from './SettingsUI';

/**
 * Administration → Settings.
 *
 * Everything about a restaurant that the restaurant itself owns, on one
 * screen, behind one Save: its name and picture, where customers pay, VAT, the
 * receipt printer, the units stock is counted in, the reminder SMS, and the
 * two switches that change what the till does on its own.
 *
 * Nothing about money owed to Dalabtire. `fee`, `tier`, `status`,
 * `nextBillDate`, `notify` and `publicBusinessId` are not on this screen, and
 * cannot be in its request — see settings.rules.js. The API refuses them too;
 * both ends knowing the rule is the point.
 *
 * ── Why it is a form with a Save rather than switches that save themselves ─
 *
 * Because most of it is typed. A screen where the boxes save on blur and the
 * switches save on click has two different rules for what "done" means, and a
 * manager halfway through renaming their restaurant has already half-renamed
 * it. One Save, one moment when the restaurant changes.
 *
 * The public menu switch also sits on the Public menu tab, next to the QR
 * code, which is where a manager looks for it. Both write the same field
 * through the same endpoint and both put the answer back in the session, so
 * the two cannot drift.
 */
export default function SettingsPanel({ onNotice }) {
  const dispatch = useDispatch();

  const activeUser = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);
  const business = useSelector((state) => state.login.business) || activeUser?.business;

  const signature = signatureOf(business);

  /**
   * `seed` is the restaurant as the server last told us it was. Everything on
   * screen is compared against it, so "changed" means changed from what is
   * saved — and it is what the request is diffed against, so a save carries
   * only the fields that actually moved.
   */
  const [seed, setSeed] = React.useState(() => ({
    signature,
    form: formFromBusiness(business),
  }));
  const [form, setForm] = React.useState(seed.form);

  const [busy, setBusy] = React.useState(false);
  const [refused, setRefused] = React.useState('');
  const [invalid, setInvalid] = React.useState('');
  const [showErrors, setShowErrors] = React.useState(false);

  const dirty = !sameForm(form, seed.form);

  /**
   * The business can change underneath this screen — the Public menu tab
   * switching the menu on, or a fresh sign-in. Pick that up, but never over
   * something half-typed: a manager renaming their restaurant should not lose
   * the name because another tab saved a switch.
   */
  React.useEffect(() => {
    if (signature === seed.signature || dirty) return;
    const next = formFromBusiness(business);
    setSeed({ signature, form: next });
    setForm(next);
  }, [signature, seed.signature, dirty, business]);

  const say = React.useCallback(
    (message, isError) => {
      if (onNotice) onNotice(message, isError);
    },
    [onNotice]
  );

  const errors = validate(form);
  const canSave = Boolean(business?._id) && dirty && !busy;

  /* ── editing ──────────────────────────────────────────────────────── */

  const clearServerWords = () => {
    setRefused('');
    setInvalid('');
  };

  const set = (patch) => {
    clearServerWords();
    setForm((current) => ({ ...current, ...patch }));
  };

  const setRow = (field, index, patch) => {
    clearServerWords();
    setForm((current) => ({
      ...current,
      [field]: current[field].map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const addRow = (field, make) =>
    setForm((current) => ({ ...current, [field]: current[field].concat([make()]) }));

  const removeRow = (field, index) =>
    setForm((current) => ({
      ...current,
      [field]: current[field].filter((_, i) => i !== index),
    }));

  const discard = () => {
    setForm(seed.form);
    setShowErrors(false);
    clearServerWords();
  };

  /* ── saving ───────────────────────────────────────────────────────── */

  const save = async () => {
    if (busy || !business?._id) return;

    if (!errors.ok) {
      setShowErrors(true);
      say(COPY.fixFirst, true);
      return;
    }

    setBusy(true);
    clearServerWords();

    try {
      // Only the fields that changed. The server takes the business from the
      // token, so there is no id to send.
      const updated = await saveBusinessSettings({ form, seed: seed.form, token });

      // The API's own instruction: replace the business with what came back.
      // Failing that, the fields that moved are folded into the copy we hold,
      // which leaves fee, nextBillDate and the rest exactly as they were.
      const merged = updated || { ...business, ...buildPayload(form, seed.form) };

      dispatch(setBusiness(merged));
      if (activeUser) dispatch(setActiveUser({ ...activeUser, business: merged }));

      const next = formFromBusiness(merged);
      setSeed({ signature: signatureOf(merged), form: next });
      setForm(next);
      setShowErrors(false);

      say(COPY.saved, false);
    } catch (error) {
      const kind = classifySaveError(error);

      if (kind === 'refused') {
        setRefused(COPY.refused);
        say(COPY.refused, true);
      } else if (kind === 'invalid') {
        const message = serverMessage(error) || COPY.failed;
        setInvalid(message);
        say(message, true);
      } else {
        say(serverMessage(error) || COPY.failed, true);
      }
    } finally {
      setBusy(false);
    }
  };

  /* ── the bits of the form that need working out ───────────────────── */

  const mark = String(form.currencyMark || '').trim();
  const preview = COPY.markPreview(mark || '$');
  // The schema lower-cases this field, so `KSh` comes back `ksh` and a manager
  // finds out at the printer. Said here, and only when it applies.
  const lowercaseNote = /[A-Z]/.test(mark) ? COPY.markLowercase(mark, mark.toLowerCase()) : '';

  const closingMenu = willCloseMenu(form, seed.form);
  const smsUsed = String(form.smsMessage || '').length;

  const serverCard = refused
    ? { title: COPY.refusedTitle, body: refused }
    : invalid
    ? { title: COPY.invalidTitle, body: invalid }
    : null;

  return (
    <div style={{ maxWidth: 860, fontFamily: FONT_STACK, paddingBottom: 8 }}>
      {/* ── heading, and the only two buttons on the screen ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 20,
          marginBottom: 18,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div style={{ fontSize: TYPE.pageTitle, fontWeight: 700 }}>{COPY.title}</div>
          <div style={{ fontSize: TYPE.body, color: T.inkSoft, marginTop: 5 }}>{COPY.intro}</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {/* Always rendered, so the two buttons do not shuffle sideways the
              moment a manager types the first letter of a new name. */}
          <span
            aria-hidden={!dirty}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              height: 28,
              padding: '0 13px',
              background: T.pendingTint,
              border: `1px solid ${T.pendingBorder}`,
              borderRadius: T.radiusPill,
              fontSize: TYPE.hint,
              fontWeight: 700,
              color: T.pendingInk,
              opacity: dirty ? 1 : 0,
              visibility: dirty ? 'visible' : 'hidden',
              transition: 'opacity 120ms linear',
            }}
          >
            <span
              style={{ width: 6, height: 6, borderRadius: '50%', background: T.pendingInk }}
            />
            {COPY.unsaved}
          </span>

          <GhostButton
            onClick={discard}
            disabled={!dirty || busy}
            style={{ height: 42, padding: '0 16px', fontSize: TYPE.button }}
          >
            {COPY.discard}
          </GhostButton>

          <PrimaryButton
            onClick={save}
            disabled={!canSave}
            busy={busy}
            style={{ height: 42, padding: '0 20px', fontSize: TYPE.button, fontWeight: 600 }}
          >
            {busy ? COPY.saving : COPY.save}
          </PrimaryButton>
        </div>
      </div>

      {serverCard ? (
        <Card
          style={{
            background: T.dangerTint,
            border: `1px solid ${T.dangerBorder}`,
            padding: 16,
          }}
        >
          <div style={{ fontSize: TYPE.body + 1, fontWeight: 700, color: T.danger }}>
            {serverCard.title}
          </div>
          <div
            style={{
              fontSize: TYPE.body,
              lineHeight: 1.65,
              color: T.ink,
              marginTop: 5,
              maxWidth: 580,
            }}
          >
            {serverCard.body}
          </div>
        </Card>
      ) : null}

      {/* ── the picture ── */}
      <LogoCard
        form={form}
        errors={errors}
        showErrors={showErrors}
        busy={busy}
        onChange={set}
        onNotice={say}
      />

      {/* ── the restaurant ── */}
      <Card>
        <SectionHead title={COPY.restaurantTitle} body={COPY.restaurantBody} />

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px', minWidth: 240 }}>
            <Field
              label={COPY.nameLabel}
              name="settings-name"
              value={form.businessName}
              onChange={(value) => set({ businessName: value })}
              placeholder={COPY.namePlaceholder}
              disabled={busy}
              invalid={showErrors && Boolean(errors.businessName)}
            />
            <FieldError>{showErrors ? errors.businessName : ''}</FieldError>
          </div>

          <div style={{ flex: '0 0 170px', minWidth: 150 }}>
            <Field
              label={COPY.markLabel}
              name="settings-mark"
              value={form.currencyMark}
              onChange={(value) => set({ currencyMark: value })}
              placeholder={COPY.markPlaceholder}
              disabled={busy}
              invalid={showErrors && Boolean(errors.currencyMark)}
            />
            <FieldError>{showErrors ? errors.currencyMark : ''}</FieldError>
          </div>
        </div>

        <Hint>
          {preview}
          {lowercaseNote ? <span style={{ marginLeft: 8 }}>{lowercaseNote}</span> : null}
        </Hint>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 18 }}>
          <div style={{ flex: '1 1 240px', minWidth: 210 }}>
            <Field
              label={COPY.numberLabel}
              name="settings-number"
              value={form.businessNumber}
              onChange={(value) => set({ businessNumber: value.replace(/[^0-9+\s-]/g, '') })}
              placeholder={COPY.numberPlaceholder}
              inputMode="tel"
              disabled={busy}
              invalid={showErrors && Boolean(errors.businessNumber)}
            />
            <FieldError>{showErrors ? errors.businessNumber : ''}</FieldError>
          </div>

          <div style={{ flex: '1 1 240px', minWidth: 210 }}>
            <Field
              label={COPY.locationLabel}
              name="settings-location"
              value={form.businessLocation}
              onChange={(value) => set({ businessLocation: value })}
              placeholder={COPY.locationPlaceholder}
              disabled={busy}
              invalid={showErrors && Boolean(errors.businessLocation)}
            />
            <FieldError>{showErrors ? errors.businessLocation : ''}</FieldError>
          </div>

          <div style={{ flex: '1 1 200px', minWidth: 180 }}>
            <Field
              label={COPY.categoryLabel}
              name="settings-category"
              value={form.businessCategory}
              onChange={(value) => set({ businessCategory: value })}
              placeholder={COPY.categoryPlaceholder}
              disabled={busy}
              invalid={showErrors && Boolean(errors.businessCategory)}
            />
            <FieldError>{showErrors ? errors.businessCategory : ''}</FieldError>
          </div>
        </div>

        <Hint>{COPY.numberHint}</Hint>
      </Card>

      {/* ── payment accounts ── */}
      <RowsCard
        title={COPY.accountsTitle}
        body={COPY.accountsBody}
        empty={COPY.accountsEmpty}
        addLabel={COPY.accountAdd}
        fullNote={COPY.accountsFull}
        footNote={COPY.cashNote}
        idPrefix="settings-account"
        columns={[
          {
            key: 'label',
            label: COPY.accountLabel,
            placeholder: COPY.accountLabelPlaceholder,
            flex: '0 0 190px',
          },
          {
            key: 'account',
            label: COPY.accountNumber,
            placeholder: COPY.accountNumberPlaceholder,
            flex: '1 1 220px',
            minWidth: 180,
          },
        ]}
        rows={form.paytoAccounts}
        errors={errors.accounts}
        showErrors={showErrors}
        busy={busy}
        full={form.paytoAccounts.length >= MAX_ACCOUNTS}
        onChange={(index, patch) => setRow('paytoAccounts', index, patch)}
        onAdd={() => addRow('paytoAccounts', blankAccount)}
        onRemove={(index) => removeRow('paytoAccounts', index)}
      />

      {/* ── VAT ── */}
      <Card>
        <SectionHead title={COPY.vatTitle} body={COPY.vatBody} />

        <div style={{ width: 160 }}>
          <Field
            label={COPY.vatLabel}
            name="settings-vat"
            value={form.vatPercentage}
            onChange={(value) =>
              // Digits and at most one point: VAT is a percentage, not a sum.
              set({
                vatPercentage: value
                  .replace(/[^0-9.]/g, '')
                  .replace(/\.(?=.*\.)/g, '')
                  .slice(0, 6),
              })
            }
            inputMode="decimal"
            suffix="%"
            disabled={busy}
            invalid={showErrors && Boolean(errors.vatPercentage)}
          />
        </div>
        <FieldError>{showErrors ? errors.vatPercentage : ''}</FieldError>
        <Hint>{COPY.vatNone}</Hint>

        <div style={{ marginTop: 20 }}>
          <SwitchRow
            divider
            label={COPY.autoVatLabel}
            body={COPY.autoVatBody}
            checked={form.autoVat}
            onChange={(value) => set({ autoVat: value })}
            disabled={busy}
          />
        </div>
      </Card>

      {/* ── the receipt printer ── */}
      <Card>
        <SectionHead title={COPY.printerTitle} body={COPY.printerBody} />

        <div style={{ maxWidth: 320 }}>
          <Field
            label={COPY.printerLabel}
            name="settings-printer"
            value={form.receiptPrinterAddress}
            onChange={(value) => set({ receiptPrinterAddress: value.replace(/\s/g, '') })}
            placeholder={COPY.printerPlaceholder}
            disabled={busy}
            invalid={showErrors && Boolean(errors.receiptPrinterAddress)}
          />
        </div>
        <FieldError>{showErrors ? errors.receiptPrinterAddress : ''}</FieldError>
        <Hint>
          {String(form.receiptPrinterAddress || '').trim() ? '' : COPY.printerClear}
        </Hint>
      </Card>

      {/* ── stock units ── */}
      <UnitsCard
        rows={form.stockUnits}
        errors={errors.units}
        showErrors={showErrors}
        busy={busy}
        full={form.stockUnits.length >= MAX_UNITS}
        onChange={(index, patch) => setRow('stockUnits', index, patch)}
        onAdd={() => addRow('stockUnits', blankUnit)}
        onRemove={(index) => removeRow('stockUnits', index)}
      />

      {/* ── SMS ── */}
      <Card>
        <SectionHead title={COPY.smsTitle} body={COPY.smsBody} />

        <Field
          label={COPY.smsLabel}
          name="settings-sms"
          value={form.smsMessage}
          onChange={(value) => set({ smsMessage: value })}
          placeholder={COPY.smsPlaceholder}
          rows={3}
          maxLength={MAX_SMS}
          disabled={busy}
          invalid={showErrors && Boolean(errors.smsMessage)}
        />
        <FieldError>{showErrors ? errors.smsMessage : ''}</FieldError>
        <Hint>
          {COPY.smsHint} <span style={{ marginLeft: 8 }}>{COPY.smsCount(smsUsed, MAX_SMS)}</span>
        </Hint>
      </Card>

      <RowsCard
        title={COPY.smsCustomTitle}
        body={COPY.smsCustomBody}
        empty={COPY.smsCustomEmpty}
        addLabel={COPY.smsCustomAdd}
        fullNote={COPY.smsCustomFull}
        footNote=""
        idPrefix="settings-sms-custom"
        columns={[
          {
            key: 'name',
            label: COPY.smsCustomName,
            placeholder: COPY.smsCustomNamePlaceholder,
            flex: '0 0 200px',
          },
          {
            key: 'message',
            label: COPY.smsCustomMessage,
            placeholder: COPY.smsPlaceholder,
            flex: '1 1 260px',
            minWidth: 220,
            rows: 2,
            maxLength: MAX_SMS,
          },
        ]}
        rows={form.smsCustomMessages}
        errors={errors.messages}
        showErrors={showErrors}
        busy={busy}
        full={form.smsCustomMessages.length >= MAX_SMS_CUSTOM}
        onChange={(index, patch) => setRow('smsCustomMessages', index, patch)}
        onAdd={() => addRow('smsCustomMessages', blankMessage)}
        onRemove={(index) => removeRow('smsCustomMessages', index)}
      />

      {/* ── the two switches. No heading: they are not a category, they are
             the two things the till does without being asked. ── */}
      <Card style={{ marginBottom: 0 }}>
        <SwitchRow
          label={COPY.autoPrintLabel}
          body={COPY.autoPrintBody}
          checked={form.autoPrintReceipt}
          onChange={(value) => set({ autoPrintReceipt: value })}
          disabled={busy}
        />

        <SwitchRow
          divider
          label={COPY.publicMenuLabel}
          body={COPY.publicMenuBody}
          checked={form.publicMenu}
          onChange={(value) => set({ publicMenu: value })}
          disabled={busy}
        />

        {closingMenu ? <WarnLine>{COPY.publicMenuClosing}</WarnLine> : null}
      </Card>
    </div>
  );
}
